import type { DepositAddress, InternalChainId, TokenSymbol } from "@cede/types";
import { API_ENDPOINT, isServerApiRfcError, CedeSDKError } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useMutation } from "@tanstack/react-query";

export type GetDepositAddressParams = {
  accountId: number;
  tokenSymbol: TokenSymbol;
  network: InternalChainId;
};

/**
 * Extracts the error message from various error types.
 * We keep the original error message as-is per project convention.
 */
const getErrorMessage = (error: unknown): string => {
  // CedeSDK errors have a message property
  if (error instanceof CedeSDKError || (error as CedeSDKError)?.isBaseError === true) {
    return (error as CedeSDKError).message || "Failed to fetch deposit address";
  }

  // RFC 9457 API errors have a detail property
  if (isServerApiRfcError(error)) {
    return error.detail || "Failed to fetch deposit address";
  }

  // Standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
};

export const useDepositAddress = () => {
  const apiHelper = useApi();
  const { auth, authenticated } = useHubUser();

  const mutation = useMutation<DepositAddress, Error, GetDepositAddressParams>({
    mutationFn: async ({ accountId, tokenSymbol, network }) => {
      if (!authenticated || !auth.identityToken) {
        throw new Error("Please connect your wallet first");
      }

      // getWithAuth throws on error (via getOrThrow), so errors are caught by onError
      return apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_DEPOSIT_ADDRESS, {
        queryParams: {
          accountId: String(accountId),
          tokenSymbol,
          network,
        },
      });
    },
    throwOnError: false,
  });

  // Extract the error message while keeping the original error
  const errorMessage = mutation.error ? getErrorMessage(mutation.error) : null;

  return {
    getDepositAddress: mutation.mutate,
    getDepositAddressAsync: mutation.mutateAsync,
    depositAddress: mutation.data,
    isLoading: mutation.isPending,
    error: errorMessage,
    reset: mutation.reset,
  };
};
